import React, { useCallback, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { crmService, Client } from '../../services/crmService';

interface QuickCaptureModalProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful capture; parent may refresh related views. */
  onCreated: (result: Client) => void;
}

const QuickCaptureModal: React.FC<QuickCaptureModalProps> = ({ open, onClose, onCreated }) => {
  const [businessName, setBusinessName] = useState('');
  const [notes, setNotes] = useState('');
  const [caption, setCaption] = useState('');
  const [location, setLocation] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [success, setSuccess] = useState<Client | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = useCallback(() => {
    setBusinessName('');
    setNotes('');
    setCaption('');
    setLocation('');
    setFile(null);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    setSuccess(null);
    setError('');
    setLoading(false);
  }, [preview]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const onPickFile = (f: File | null) => {
    if (!f) return;
    if (!/^image\/(jpeg|jpg|png|webp|heic|heif)$/i.test(f.type)) {
      setError('Please choose a JPEG, PNG, WebP, or HEIC image.');
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      setError('Image must be 5 MB or smaller.');
      return;
    }
    setError('');
    setFile(f);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(URL.createObjectURL(f));
  };

  const useGeo = () => {
    if (!navigator.geolocation) {
      setError('Location is not available on this device.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation(`${pos.coords.latitude.toFixed(5)}, ${pos.coords.longitude.toFixed(5)}`);
        setError('');
      },
      () => setError('Could not read location — you can type or leave blank.'),
      { enableHighAccuracy: false, timeout: 10000 }
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setError('Add a photo first.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('image', file);
      if (businessName.trim()) fd.append('business_name', businessName.trim());
      if (notes.trim()) fd.append('notes', notes.trim());
      if (caption.trim()) fd.append('caption', caption.trim());
      if (location.trim()) fd.append('location', location.trim());
      const result = await crmService.quickCaptureLead(fd);
      setSuccess(result);
      onCreated(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save capture');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="modal-backdrop z-[60]">
      <div className="relative top-4 mx-auto max-h-[92vh] w-full max-w-lg overflow-y-auto border border-border bg-surface p-5 shadow-lg rounded-2xl">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-fg">Photo capture</h3>
            <p className="text-sm text-fg-muted mt-1">
              Snap a business card or photo now. Captures land in your Pipeline for review and follow-up.
            </p>
          </div>
          <button type="button" onClick={handleClose} className="text-fg-muted hover:text-fg" aria-label="Close">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error ? <p className="mb-3 text-sm text-danger-fg">{error}</p> : null}
        {success ? (
          <div className="mb-3 rounded-xl border border-success-fg/30 bg-success-fg/10 p-3 text-sm text-fg">
            <p className="font-medium">Saved to your pipeline.</p>
            <p className="mt-1 text-fg-muted">
              New lead created: {success.full_name}
            </p>
            <Link
              to={`/leads?tab=pipeline&pipeline_contact=${success.id}`}
              className="mt-2 inline-block text-primary hover:underline"
            >
              Open lead in pipeline
            </Link>
          </div>
        ) : null}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-fg-muted mb-1">Business/Lead Name (optional)</label>
            <input
              type="text"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              placeholder="e.g. Acme Plumbing"
              className="input-field w-full rounded-xl border border-border bg-input px-3 py-2 text-sm text-fg"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif"
              capture="environment"
              className="hidden"
              onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif"
              className="hidden"
              onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
            />
            <button
              type="button"
              onClick={() => cameraInputRef.current?.click()}
              className="btn-primary text-sm py-2 px-4 rounded-xl"
            >
              Take photo
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="btn-secondary text-sm py-2 px-4 rounded-xl"
            >
              Upload image
            </button>
          </div>

          {preview ? (
            <div className="rounded-xl border border-border overflow-hidden max-h-64 bg-app">
              <img src={preview} alt="Preview" className="w-full h-auto object-contain max-h-64" />
            </div>
          ) : (
            <p className="text-xs text-fg-muted">No photo yet — use camera or upload.</p>
          )}

          <div>
            <label className="block text-xs font-medium text-fg-muted mb-1">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="e.g. Met at Chamber event"
              className="input-field w-full rounded-xl border border-border bg-input px-3 py-2 text-sm text-fg"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-fg-muted mb-1">Caption (optional)</label>
            <input
              type="text"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Caption shown on lead image"
              className="input-field w-full rounded-xl border border-border bg-input px-3 py-2 text-sm text-fg"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-fg-muted mb-1">Location (optional)</label>
            <div className="flex gap-2 flex-wrap">
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Address or area"
                className="input-field flex-1 min-w-[12rem] rounded-xl border border-border bg-input px-3 py-2 text-sm text-fg"
              />
              <button type="button" onClick={useGeo} className="btn-secondary text-sm py-2 px-3 rounded-xl shrink-0">
                Use GPS
              </button>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={handleClose} className="btn-surface-muted rounded-xl px-4 py-2 text-sm">
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !file}
              className="btn-primary rounded-xl px-4 py-2 text-sm disabled:opacity-50"
            >
              {loading ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuickCaptureModal;
